import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { SupportedModel, ParameterDescriptor } from '@/redux/internal-services/agentsApi';
import {
  AgentFormValues,
  SectionCard,
  DescriptorField,
  inputCls,
  labelCls,
} from './agentShared';

interface OverviewTabProps {
  isEdit: boolean;
  models: SupportedModel[];
  paramGroups: Record<string, ParameterDescriptor[]>;
}

const OverviewTab: React.FC<OverviewTabProps> = ({ isEdit, models, paramGroups }) => {
  const {
    register,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<AgentFormValues>();

  const agentType = watch('agent_type');
  const isWorkflow = agentType === 'workflow';
  const modelParams = watch('model_parameters') || {};

  const setParam = (key: string, value: unknown) =>
    setValue('model_parameters', { ...modelParams, [key]: value }, { shouldDirty: true });

  const paramDescriptors = paramGroups['model_parameters'] || [];

  return (
    <>
      {/* Identity */}
      <SectionCard title="Identity" desc="How this agent is named, typed, and triggered.">
        <div className="space-y-4">
          <div>
            <label className={labelCls}>
              Agent name <span className="text-[#CF4148]">*</span>
            </label>
            <input
              className={`${inputCls} ${errors.agent_name ? 'border-[#CF4148] focus:ring-[#CF4148]' : ''}`}
              placeholder="e.g. SAM, ScheduleBot"
              {...register('agent_name')}
            />
            {errors.agent_name && (
              <p className="mt-1 text-xs text-[#CF4148]">{errors.agent_name.message}</p>
            )}
          </div>

          <div>
            <label className={labelCls}>Description</label>
            <textarea
              className={inputCls}
              rows={2}
              placeholder="Short description of what this agent does."
              {...register('description')}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>
                Agent type
                {isEdit && (
                  <span className="ml-1 text-[11px] text-ui-text3 font-normal">— fixed at creation</span>
                )}
              </label>
              {isEdit ? (
                <div className={`${inputCls} bg-ui-input text-ui-text2 cursor-default`}>
                  {agentType === 'workflow' ? 'Workflow (Automation)' : 'Conversational (Chat)'}
                </div>
              ) : (
                <select className={inputCls} {...register('agent_type')}>
                  <option value="conversational">Conversational (Chat)</option>
                  <option value="workflow">Workflow (Automation)</option>
                </select>
              )}
            </div>

            {isWorkflow && (
              <div>
                <label className={labelCls}>Trigger type</label>
                <select className={inputCls} {...register('trigger_type')}>
                  <option value="manual">Manual</option>
                  <option value="schedule">Schedule</option>
                  <option value="webhook">Webhook</option>
                  <option value="event">Event</option>
                </select>
              </div>
            )}
          </div>
        </div>
      </SectionCard>

      {/* Model & Status */}
      <SectionCard title="Model" desc="The LLM powering this agent and its operational status.">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>
              Model <span className="text-[#CF4148]">*</span>
            </label>
            <select
              className={`${inputCls} ${errors.models?.text ? 'border-[#CF4148] focus:ring-[#CF4148]' : ''}`}
              {...register('models.text')}
            >
              {models.map((m) => (
                <option key={m.model_id} value={m.name}>{m.name}</option>
              ))}
            </select>
            {errors.models?.text && (
              <p className="mt-1 text-xs text-[#CF4148]">{errors.models.text.message}</p>
            )}
          </div>

          <div>
            <label className={labelCls}>Status</label>
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <div className="flex items-center gap-3 h-10">
                  <button
                    type="button"
                    onClick={() => field.onChange(field.value === 'active' ? 'disabled' : 'active')}
                    className={`inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm font-medium transition ${
                      field.value === 'active'
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                        : 'border-ui-border bg-ui-input text-ui-text2'
                    }`}
                  >
                    <span className={`h-1.5 w-1.5 rounded-full ${field.value === 'active' ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                    {field.value === 'active' ? 'Active' : 'Disabled'}
                  </button>
                  <span className="text-xs text-ui-text3">Click to toggle</span>
                </div>
              )}
            />
          </div>
        </div>
      </SectionCard>

      {/* Classification */}
      <SectionCard title="Classification" desc="Used for filtering and discovery in the agent catalogue.">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Category</label>
            <input
              className={inputCls}
              placeholder="e.g. Sales, Support, Billing"
              {...register('category')}
            />
          </div>
          <div>
            <label className={labelCls}>
              Tags <span className="text-ui-text3 font-normal text-xs">— comma-separated</span>
            </label>
            <Controller
              name="tags"
              control={control}
              render={({ field }) => (
                <input
                  className={inputCls}
                  placeholder="e.g. crm, voice, demo"
                  value={(field.value || []).join(', ')}
                  onChange={(e) =>
                    field.onChange(
                      e.target.value.split(',').map((s) => s.trim()).filter(Boolean)
                    )
                  }
                />
              )}
            />
          </div>
        </div>
      </SectionCard>

      {/* Model parameters */}
      {paramDescriptors.length > 0 && (
        <SectionCard
          title="Model parameters"
          desc="Schema-driven tuning for the underlying model. These defaults apply unless overridden."
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {paramDescriptors.map((d) => (
              <DescriptorField
                key={d.key}
                d={d}
                value={modelParams[d.key] ?? d.default}
                onChange={(v) => setParam(d.key, v)}
              />
            ))}
          </div>
        </SectionCard>
      )}
    </>
  );
};

export default OverviewTab;
